/**
 * Part 5 - every registered site, read from the site registry.
 *
 * A site is a folder with a site.yaml under backend/app/sites/; this table is
 * how a newly dropped folder is seen, and how a broken one is reported rather
 * than silently missing.
 */

import { useQuery } from '@tanstack/react-query'

import { api } from '../api/client'

interface SiteSummary {
  id: string
  display_name: string
  state: string | null
  river: string | null
  hazard_class: string
  structure_types: string[]
  india_region: string
  crs: string
  reservoir_volume: { value: number | null; unit: string; status: string; reason: string | null }
  dem_available: boolean
  folder: string
}

interface SitesPayload {
  default: string
  sites: SiteSummary[]
  errors: { folder: string; error: string }[]
  india_regions_note: string
}

const pretty = (s: string) => s.replace(/_/g, ' ').toLowerCase()

export function SitesPanel() {
  const q = useQuery<SitesPayload>({ queryKey: ['sites'], queryFn: () => api.raw('/sites') })
  return (
    <section className="mt-5">
      <div className="label mb-1">Registered sites</div>
      {q.isLoading ? (
        <div className="skeleton h-20 w-full" />
      ) : q.isError || !q.data ? (
        <div className="empty-slot">
          <div className="empty-title">Site registry unavailable</div>
          {String(q.error)}
        </div>
      ) : (
        <>
          <table className="grid-table w-full">
            <thead>
              <tr>
                <th className="text-left">site</th>
                <th className="text-left">state · river</th>
                <th className="text-left">hazard</th>
                <th className="text-left">structure</th>
                <th className="text-left">region (team)</th>
                <th className="text-left">CRS</th>
                <th className="text-left">DEM</th>
                <th className="num">volume Mm³</th>
              </tr>
            </thead>
            <tbody>
              {q.data.sites.map((s) => (
                <tr key={s.id}>
                  <td style={{ color: 'var(--ink-text)' }}>
                    {s.display_name}
                    {s.id === q.data.default && (
                      <span style={{ color: 'var(--ink-text-faint)' }}> · default</span>
                    )}
                  </td>
                  <td>{[s.state, s.river].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{pretty(s.hazard_class)}</td>
                  <td>{s.structure_types.map(pretty).join(', ')}</td>
                  <td>{pretty(s.india_region)}</td>
                  <td className="num" style={{ textAlign: 'left' }}>{s.crs}</td>
                  <td>{s.dem_available ? 'present' : 'absent'}</td>
                  <td className="num" title={s.reservoir_volume.reason ?? undefined}>
                    {s.reservoir_volume.value != null
                      ? (s.reservoir_volume.value / 1e6).toLocaleString('en-IN', { maximumFractionDigits: 0 })
                      : s.reservoir_volume.status}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {q.data.errors.map((e) => (
            <p key={e.folder} className="mt-1 text-12" style={{ color: 'var(--ink-warn)' }}>
              {e.folder}/site.yaml did not load: {e.error}
            </p>
          ))}
          <p className="mt-1 text-12" style={{ color: 'var(--ink-text-faint)' }}>
            {q.data.india_regions_note} A site is added by dropping a folder with a site.yaml into
            backend/app/sites/ — no code change.
          </p>
        </>
      )}
    </section>
  )
}
